import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowDown,
  ArrowUp,
  Download,
  Eye,
  EyeOff,
  Loader2,
  Merge,
  Plus,
  RotateCcw,
  Trash2,
  Upload,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { api, downloadPdf, errMsg, photoSrc } from "../../lib/api";
import { readFile } from "../../lib/image";
import { liveSummary } from "../../lib/summary";

const BASE_SECTIONS = [
  ["executive_summary", "Executive Summary"],
  ["property_details", "Property Details"],
  ["score", "Overall Property Score"],
  ["room_summary", "Room-wise Summary"],
  ["reinspection", "Before vs After Comparison"],
  ["detailed_observations", "Detailed Observations"],
  ["rectification_summary", "Rectification Summary"],
  ["limitations", "Inspection Limitations"],
  ["disclaimer", "Disclaimer"],
  ["contact", "Company Contact"],
];

const emptyConfig = (config) => ({
  sections: config?.sections?.length
    ? config.sections
    : BASE_SECTIONS.map(([id, title]) => ({ id, title, visible: true })),
  hidden_room_ids: config?.hidden_room_ids || [],
  hidden_finding_ids: config?.hidden_finding_ids || [],
  priority_override: config?.priority_override || false,
  priority_finding_ids: config?.priority_finding_ids || [],
  room_status_overrides: config?.room_status_overrides || {},
  scoring_explanation: config?.scoring_explanation || "",
  score_override_note: config?.score_override_note || "",
  score_override_meta: config?.score_override_meta || null,
  final_score: config?.final_score ?? null,
});

const SEVERITY_ORDER = { MAJOR: 0, MINOR: 1, COSMETIC: 2, OBSERVATION: 3 };

const syncedRoomResults = (rooms, defects, itemIds) =>
  rooms.map((room) => ({
    ...room,
    items: room.items.map((item) => {
      if (!itemIds.has(item.item_id)) return item;
      const linked = defects.filter(
        (defect) =>
          defect.item_id === item.item_id && defect.room_id === room.room_id,
      );
      if (!linked.length) return { ...item, result: "PASS" };
      const severity = [...linked].sort(
        (a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity],
      )[0].severity;
      return { ...item, result: severity };
    }),
  }));

const cloneInspection = (value) => ({
  ...value,
  customer: { ...(value.customer || {}) },
  property: { ...(value.property || {}) },
  final_notes: { ...(value.final_notes || {}) },
  rooms: (value.rooms || []).map((room) => ({
    ...room,
    items: (room.items || []).map((item) => ({ ...item })),
  })),
  defects: (value.defects || []).map((defect) => ({
    ...defect,
    photos: [...(defect.photos || [])],
  })),
  report_config: emptyConfig(value.report_config),
});

function Field({ label, value, onChange, testid, type = "text" }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input
        type={type}
        value={value ?? ""}
        onChange={(event) => onChange(event.target.value)}
        className="mt-1.5 h-10"
        data-testid={testid}
      />
    </div>
  );
}

const moveItem = (items, currentIndex, direction) => {
  const nextIndex = currentIndex + direction;
  if (nextIndex < 0 || nextIndex >= items.length) return items;
  const reordered = [...items];
  [reordered[currentIndex], reordered[nextIndex]] = [
    reordered[nextIndex],
    reordered[currentIndex],
  ];
  return reordered;
};

const reportSignature = (report) =>
  JSON.stringify({
    customer: report?.customer,
    property: report?.property,
    rooms: report?.rooms,
    defects: report?.defects,
    final_notes: report?.final_notes,
    report_config: report?.report_config,
  });

export default function ReportEditor() {
  const { iid } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");
  const [consistency, setConsistency] = useState(null);
  const [mergeTargets, setMergeTargets] = useState({});
  const [auditTrail, setAuditTrail] = useState([]);
  const [savedSignature, setSavedSignature] = useState("");

  const loadAuditTrail = useCallback(() => {
    api
      .get("/admin/audit")
      .then(({ data }) => {
        setAuditTrail(
          data.filter((entry) => entry.target === iid).slice(0, 12),
        );
      })
      .catch(() => {});
  }, [iid]);

  useEffect(() => {
    api
      .get(`/inspections/${iid}`)
      .then(({ data }) => {
        const clone = cloneInspection(data);
        setReport(clone);
        setSavedSignature(reportSignature(clone));
      })
      .catch((error) => toast.error(errMsg(error)));
    api
      .get(`/inspections/${iid}/report/consistency`)
      .then(({ data }) => setConsistency(data))
      .catch(() => {});
    loadAuditTrail();
  }, [iid, loadAuditTrail]);

  const reportSummary = useMemo(
    () =>
      report
        ? liveSummary(
            report,
            report.report_config?.hidden_room_ids || [],
            report.report_config?.hidden_finding_ids || [],
          )
        : null,
    [report],
  );
  const calculatedScore = reportSummary?.score ?? 0;
  const hiddenRooms = useMemo(
    () => new Set(report?.report_config?.hidden_room_ids || []),
    [report],
  );
  const hiddenFindings = useMemo(
    () => new Set(report?.report_config?.hidden_finding_ids || []),
    [report],
  );
  const activeFindings = useMemo(
    () =>
      (report?.defects || []).filter(
        (defect) =>
          !hiddenFindings.has(defect.defect_id) &&
          !hiddenRooms.has(defect.room_id),
      ),
    [report, hiddenFindings, hiddenRooms],
  );
  const hasUnsavedChanges =
    Boolean(report) && reportSignature(report) !== savedSignature;

  const change = (path, value) => {
    setReport((current) => ({
      ...current,
      [path]: value,
    }));
  };

  const changeObject = (name, key, value) => {
    setReport((current) => ({
      ...current,
      [name]: { ...current[name], [key]: value },
    }));
  };

  const updateConfig = (fn) => {
    setReport((current) => ({
      ...current,
      report_config: fn(current.report_config),
    }));
  };

  const updateSection = (id, patch) => {
    updateConfig((config) => ({
      ...config,
      sections: config.sections.map((section) =>
        section.id === id ? { ...section, ...patch } : section,
      ),
    }));
  };

  const toggleRoom = (roomId) => {
    updateConfig((config) => ({
      ...config,
      hidden_room_ids: config.hidden_room_ids.includes(roomId)
        ? config.hidden_room_ids.filter((id) => id !== roomId)
        : [...config.hidden_room_ids, roomId],
    }));
  };

  const moveRoom = (roomId, direction) => {
    setReport((current) => {
      const index = current.rooms.findIndex((room) => room.room_id === roomId);
      return { ...current, rooms: moveItem(current.rooms, index, direction) };
    });
  };

  const addRoom = () => {
    change("rooms", [
      ...report.rooms,
      {
        room_id: `rm_edit_${Date.now().toString(36)}`,
        name: "New area",
        type: "custom",
        items: [],
      },
    ]);
  };

  const updateRoom = (roomId, patch) => {
    change(
      "rooms",
      report.rooms.map((room) =>
        room.room_id === roomId ? { ...room, ...patch } : room,
      ),
    );
  };

  const updateItem = (roomId, itemId, patch) => {
    change(
      "rooms",
      report.rooms.map((room) =>
        room.room_id !== roomId
          ? room
          : {
              ...room,
              items: room.items.map((item) =>
                item.item_id === itemId ? { ...item, ...patch } : item,
              ),
            },
      ),
    );
  };

  const removeItem = (roomId, itemId) => {
    change(
      "rooms",
      report.rooms.map((room) =>
        room.room_id !== roomId
          ? room
          : {
              ...room,
              items: room.items.filter((item) => item.item_id !== itemId),
            },
      ),
    );
  };

  const addItem = (roomId) => {
    updateRoom(roomId, {
      items: [
        ...(report.rooms.find((room) => room.room_id === roomId)?.items || []),
        {
          item_id: `it_edit_${Date.now().toString(36)}`,
          category: "General Functionality",
          name: "New checklist item",
          result: "PASS",
          note: "",
        },
      ],
    });
  };

  const updateFinding = (defectId, patch) => {
    setReport((current) => {
      const previous = current.defects.find(
        (defect) => defect.defect_id === defectId,
      );
      const defects = current.defects.map((defect) =>
        defect.defect_id === defectId ? { ...defect, ...patch } : defect,
      );
      const itemIds = new Set(
        [previous?.item_id, patch.item_id].filter(Boolean),
      );
      return {
        ...current,
        defects,
        rooms: syncedRoomResults(current.rooms, defects, itemIds),
      };
    });
  };

  const removeFinding = (defectId) => {
    setReport((current) => {
      const removed = current.defects.find(
        (defect) => defect.defect_id === defectId,
      );
      const defects = current.defects.filter(
        (defect) => defect.defect_id !== defectId,
      );
      const itemIds = new Set([removed?.item_id].filter(Boolean));
      return {
        ...current,
        defects,
        rooms: syncedRoomResults(current.rooms, defects, itemIds),
      };
    });
  };

  const toggleFindingVisibility = (defectId) => {
    updateConfig((config) => ({
      ...config,
      hidden_finding_ids: config.hidden_finding_ids.includes(defectId)
        ? config.hidden_finding_ids.filter((id) => id !== defectId)
        : [...config.hidden_finding_ids, defectId],
    }));
  };

  const moveFinding = (defectId, direction) => {
    setReport((current) => {
      const index = current.defects.findIndex(
        (defect) => defect.defect_id === defectId,
      );
      return {
        ...current,
        defects: moveItem(current.defects, index, direction),
      };
    });
  };

  const mergeFinding = (sourceId) => {
    const targetId = mergeTargets[sourceId];
    if (!targetId || targetId === sourceId) {
      toast.error("Choose another finding to merge into");
      return;
    }
    setReport((current) => {
      const source = current.defects.find(
        (defect) => defect.defect_id === sourceId,
      );
      const target = current.defects.find(
        (defect) => defect.defect_id === targetId,
      );
      if (!source || !target) return current;
      const severity =
        SEVERITY_ORDER[source.severity] < SEVERITY_ORDER[target.severity]
          ? source.severity
          : target.severity;
      const mergedText = [target.remark, source.remark]
        .filter(Boolean)
        .filter((value, index, values) => values.indexOf(value) === index)
        .join("\n\n");
      const defects = current.defects.map((defect) =>
        defect.defect_id === targetId
          ? {
              ...defect,
              severity,
              remark: mergedText,
              photos: [
                ...new Set([
                  ...(defect.photos || []),
                  ...(source.photos || []),
                ]),
              ],
            }
          : defect,
      );
      return {
        ...current,
        defects,
        report_config: {
          ...current.report_config,
          hidden_finding_ids: [
            ...new Set([...current.report_config.hidden_finding_ids, sourceId]),
          ],
        },
      };
    });
    toast.success(
      "Source finding hidden; its photos and observation were merged",
    );
  };

  const addFinding = () => {
    const room = report.rooms[0];
    if (!room) {
      toast.error("Add a room before adding a finding");
      return;
    }
    const itemId = `it_edit_${Date.now().toString(36)}`;
    const findingId = `df_edit_${Date.now().toString(36)}`;
    const nextRooms = report.rooms.map((entry) =>
      entry.room_id !== room.room_id
        ? entry
        : {
            ...entry,
            items: [
              ...entry.items,
              {
                item_id: itemId,
                category: "General Functionality",
                name: "New finding",
                result: "MINOR",
                note: "",
              },
            ],
          },
    );
    setReport((current) => ({
      ...current,
      rooms: nextRooms,
      defects: [
        ...current.defects,
        {
          defect_id: findingId,
          room_id: room.room_id,
          room: room.name,
          item_id: itemId,
          item: "New finding",
          category: "General Functionality",
          name: "New finding",
          severity: "MINOR",
          remark: "",
          recommendation: "",
          photos: [],
          created_at: new Date().toISOString(),
        },
      ],
    }));
  };

  const uploadPhoto = async (defectId, file) => {
    if (!file) return;
    setUploading(defectId);
    try {
      const dataUrl = await readFile(file);
      const { data } = await api.post("/photos", {
        inspection_id: iid,
        data_url: dataUrl,
      });
      const finding = report.defects.find(
        (defect) => defect.defect_id === defectId,
      );
      updateFinding(defectId, {
        photos: [...(finding?.photos || []), data.photo_id],
      });
      toast.success("Photo attached to the report finding");
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setUploading("");
    }
  };

  const save = async (thenDownload = false, thenPreview = false) => {
    setSaving(true);
    try {
      const payload = {
        customer: report.customer,
        property: report.property,
        rooms: report.rooms,
        defects: report.defects,
        final_notes: report.final_notes,
        report_config: report.report_config,
      };
      const { data } = await api.put(`/inspections/${iid}/report`, payload);
      setReport(cloneInspection(data));
      setSavedSignature(reportSignature(cloneInspection(data)));
      setConsistency(data.report_consistency || null);
      loadAuditTrail();
      toast.success("Report edits saved");
      if (thenDownload) await downloadPdf(iid);
      if (thenPreview) navigate(`/admin/inspections/${iid}`);
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setSaving(false);
    }
  };

  if (!report) {
    return (
      <div
        className="grid place-items-center py-24"
        data-testid="report-editor-loading"
      >
        <Loader2 className="h-6 w-6 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl pb-20" data-testid="admin-report-editor">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline mb-2">Admin report control</div>
          <h1 className="font-display text-3xl font-extrabold">
            Edit report · {iid}
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            Saved changes are used for the online report and every regenerated
            PDF.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            to={`/admin/inspections/${iid}`}
            className="tap flex h-10 items-center gap-2 rounded-md border border-slate-200 px-4 text-sm font-semibold"
            data-testid="report-editor-cancel-link"
          >
            Back to report
          </Link>
          <button
            onClick={() => save(false, true)}
            disabled={saving}
            className="tap flex h-10 items-center gap-2 rounded-md border border-slate-900 px-4 text-sm font-semibold"
            data-testid="report-editor-preview-button"
          >
            <Eye className="h-4 w-4" /> Preview
          </button>
          <button
            onClick={() => save(true)}
            disabled={saving}
            className="tap flex h-10 items-center gap-2 rounded-md bg-teal-500 px-4 text-sm font-bold text-slate-950"
            data-testid="report-editor-regenerate-pdf-button"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            Regenerate PDF
          </button>
          <button
            onClick={() => save(false)}
            disabled={saving}
            className="tap h-10 rounded-md bg-slate-900 px-4 text-sm font-bold text-white"
            data-testid="report-editor-save-button"
          >
            Save
          </button>
        </div>
      </div>

      <div className="space-y-6">
        <section className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="overline mb-4">Customer and property</div>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field
              label="Customer name"
              value={report.customer.name}
              testid="report-customer-name-input"
              onChange={(value) => changeObject("customer", "name", value)}
            />
            <Field
              label="Customer phone"
              value={report.customer.phone}
              testid="report-customer-phone-input"
              onChange={(value) => changeObject("customer", "phone", value)}
            />
            <Field
              label="Customer email"
              value={report.customer.email}
              testid="report-customer-email-input"
              onChange={(value) => changeObject("customer", "email", value)}
            />
            <Field
              label="Project"
              value={report.property.project}
              testid="report-project-input"
              onChange={(value) => changeObject("property", "project", value)}
            />
            <Field
              label="Builder"
              value={report.property.builder}
              testid="report-builder-input"
              onChange={(value) => changeObject("property", "builder", value)}
            />
            <Field
              label="Location"
              value={report.property.location}
              testid="report-location-input"
              onChange={(value) => changeObject("property", "location", value)}
            />
            <Field
              label="BHK"
              value={report.property.bhk}
              testid="report-bhk-input"
              onChange={(value) => changeObject("property", "bhk", value)}
            />
            <Field
              label="Bathrooms"
              value={report.property.bathroom_count}
              testid="report-bathroom-count-input"
              onChange={(value) =>
                changeObject("property", "bathroom_count", value)
              }
            />
            <Field
              label="Floor"
              value={report.property.floor}
              testid="report-floor-input"
              onChange={(value) => changeObject("property", "floor", value)}
            />
            <Field
              label="Total floors"
              value={report.property.total_floors}
              testid="report-total-floors-input"
              onChange={(value) =>
                changeObject("property", "total_floors", value)
              }
            />
            <Field
              label="Tower / block"
              value={report.property.tower}
              testid="report-tower-input"
              onChange={(value) => changeObject("property", "tower", value)}
            />
            <Field
              label="Flat number"
              value={report.property.flat}
              testid="report-flat-input"
              onChange={(value) => changeObject("property", "flat", value)}
            />
          </div>
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="overline mb-4">Score approval</div>
          <div className="grid gap-4 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
            <Field
              label="System calculated score"
              value={`${calculatedScore} / 100`}
              testid="report-calculated-score"
              onChange={() => {}}
            />
            <Field
              label="Admin final score"
              type="number"
              value={report.report_config.final_score ?? ""}
              testid="report-final-score-input"
              onChange={(value) =>
                updateConfig((config) => ({ ...config, final_score: value }))
              }
            />
            <button
              onClick={() =>
                updateConfig((config) => ({ ...config, final_score: null }))
              }
              className="tap h-10 rounded-md border border-slate-200 px-4 text-sm font-semibold"
              data-testid="report-clear-score-override-button"
            >
              Use calculated
            </button>
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div>
              <Label className="text-xs">Override note / reason</Label>
              <Textarea
                rows={2}
                value={report.report_config.score_override_note || ""}
                onChange={(event) =>
                  updateConfig((config) => ({
                    ...config,
                    score_override_note: event.target.value,
                  }))
                }
                data-testid="report-score-override-note-input"
              />
            </div>
            <div>
              <Label className="text-xs">
                Scoring explanation shown in report
              </Label>
              <Textarea
                rows={2}
                value={report.report_config.scoring_explanation || ""}
                placeholder="Default calculation explanation is used when left blank."
                onChange={(event) =>
                  updateConfig((config) => ({
                    ...config,
                    scoring_explanation: event.target.value,
                  }))
                }
                data-testid="report-scoring-explanation-input"
              />
            </div>
          </div>
          {report.report_config.score_override_meta && (
            <p
              className="mt-3 text-xs text-slate-500"
              data-testid="report-score-override-status"
            >
              Admin override:{" "}
              {report.report_config.score_override_meta.calculated_score}/100
              calculated →{" "}
              {report.report_config.score_override_meta.final_score}/100 final.
            </p>
          )}
        </section>

        <section
          className="rounded-xl border border-slate-200 bg-white p-5"
          data-testid="report-consistency-status"
        >
          <div className="overline mb-2">Final report consistency</div>
          {consistency?.ok ? (
            <p className="text-sm font-semibold text-emerald-700">
              {hasUnsavedChanges
                ? "Unsaved changes are shown in the editor. Save to rerun the final check."
                : "Ready to regenerate."}{" "}
              Checklist outcomes, room totals and{" "}
              {hasUnsavedChanges
                ? activeFindings.length
                : consistency.active_findings}{" "}
              active{" "}
              {(hasUnsavedChanges
                ? activeFindings.length
                : consistency.active_findings) === 1
                ? "finding"
                : "findings"}{" "}
              reconcile.
            </p>
          ) : (
            <p className="text-sm font-semibold text-rose-700">
              {consistency?.issues?.join(" ") ||
                "Save changes to run the consistency check."}
            </p>
          )}
        </section>

        <section
          className="rounded-xl border border-slate-200 bg-white p-5"
          data-testid="report-audit-trail"
        >
          <div className="overline mb-3">Admin change history</div>
          {auditTrail.length ? (
            <div className="space-y-2">
              {auditTrail.map((entry) => (
                <div
                  key={entry.log_id}
                  className="flex flex-wrap justify-between gap-2 border-b border-slate-100 pb-2 text-sm last:border-0"
                >
                  <span>
                    <b>{entry.user_name || "Admin"}</b> ·{" "}
                    {entry.detail || entry.action}
                  </span>
                  <span className="font-mono text-xs text-slate-400">
                    {new Date(entry.at).toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500">
              No saved admin changes yet.
            </p>
          )}
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="overline">Final report sections</div>
              <p className="mt-1 text-sm text-slate-600">
                Hide, restore, rename, or add sections without deleting source
                inspection data.
              </p>
            </div>
            <button
              onClick={() =>
                updateConfig((config) => ({
                  ...config,
                  sections: [
                    ...config.sections,
                    {
                      id: `custom_${Date.now().toString(36)}`,
                      title: "Additional Information",
                      content: "",
                      visible: true,
                    },
                  ],
                }))
              }
              className="tap flex h-10 items-center gap-2 rounded-md border border-slate-900 px-4 text-sm font-semibold"
              data-testid="report-add-section-button"
            >
              <Plus className="h-4 w-4" />
              Add section
            </button>
          </div>
          <div className="mt-4 space-y-2">
            {report.report_config.sections.map((section) => (
              <div
                key={section.id}
                className="grid gap-3 rounded-md border border-slate-200 p-3 md:grid-cols-[1fr_auto_auto] md:items-center"
                data-testid={`report-section-${section.id}`}
              >
                <div className="space-y-2">
                  <Input
                    value={section.title}
                    onChange={(event) =>
                      updateSection(section.id, { title: event.target.value })
                    }
                    data-testid={`report-section-title-${section.id}`}
                  />
                  {section.id.startsWith("custom_") && (
                    <Textarea
                      rows={3}
                      value={section.content || ""}
                      onChange={(event) =>
                        updateSection(section.id, {
                          content: event.target.value,
                        })
                      }
                      data-testid={`report-section-content-${section.id}`}
                    />
                  )}
                </div>
                <button
                  onClick={() =>
                    updateSection(section.id, { visible: !section.visible })
                  }
                  className="tap h-10 rounded-md border border-slate-200 px-3 text-sm font-semibold"
                  data-testid={`report-section-toggle-${section.id}`}
                >
                  {section.visible ? "Hide" : "Restore"}
                </button>
                {section.id.startsWith("custom_") && (
                  <button
                    onClick={() =>
                      updateConfig((config) => ({
                        ...config,
                        sections: config.sections.filter(
                          (entry) => entry.id !== section.id,
                        ),
                      }))
                    }
                    className="tap grid h-10 w-10 place-items-center rounded-md border border-rose-200 text-rose-600"
                    data-testid={`report-section-remove-${section.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="overline mb-2">Executive summary</div>
          <Textarea
            rows={5}
            value={report.final_notes.executive_summary || ""}
            onChange={(event) => {
              changeObject(
                "final_notes",
                "executive_summary",
                event.target.value,
              );
              changeObject("final_notes", "executive_summary_mode", "custom");
            }}
            data-testid="report-executive-summary-input"
          />
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="overline">Top priority issues</div>
              <p className="mt-1 text-sm text-slate-600">
                Priorities use active findings only. Set a manual order when the
                automatic severity order needs review.
              </p>
            </div>
            <button
              onClick={() =>
                updateConfig((config) => ({
                  ...config,
                  priority_override: false,
                  priority_finding_ids: [],
                }))
              }
              className="tap flex h-10 items-center gap-2 rounded-md border border-slate-200 px-3 text-sm font-semibold"
              data-testid="report-priority-use-automatic-button"
            >
              <RotateCcw className="h-4 w-4" />
              Use automatic order
            </button>
          </div>
          <div className="mt-4 space-y-2">
            {(report.report_config.priority_override
              ? report.report_config.priority_finding_ids
                  .map((id) =>
                    activeFindings.find((finding) => finding.defect_id === id),
                  )
                  .filter(Boolean)
              : [...activeFindings]
                  .sort(
                    (a, b) =>
                      SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity],
                  )
                  .slice(0, 5)
            ).map((finding, index) => (
              <div
                key={finding.defect_id}
                className="flex flex-wrap items-center gap-2 rounded-md border border-slate-200 p-3"
                data-testid={`report-priority-${finding.defect_id}`}
              >
                <span className="font-mono text-xs font-bold text-slate-500">
                  {index + 1}
                </span>
                <span className="font-mono text-xs font-semibold">
                  {finding.number || "PC-NEW"}
                </span>
                <span className="font-semibold">{finding.name}</span>
                <span className="text-sm text-slate-500">{finding.room}</span>
                <span className="ml-auto text-xs font-bold text-slate-600">
                  {finding.severity}
                </span>
                <button
                  onClick={() =>
                    updateConfig((config) => {
                      const ids = config.priority_override
                        ? config.priority_finding_ids.filter(
                            (id) => id !== finding.defect_id,
                          )
                        : activeFindings
                            .sort(
                              (a, b) =>
                                SEVERITY_ORDER[a.severity] -
                                SEVERITY_ORDER[b.severity],
                            )
                            .slice(0, 5)
                            .map((entry) => entry.defect_id)
                            .filter((id) => id !== finding.defect_id);
                      return {
                        ...config,
                        priority_override: true,
                        priority_finding_ids: ids,
                      };
                    })
                  }
                  className="tap h-8 rounded-md border border-slate-200 px-2 text-xs font-semibold"
                  data-testid={`report-priority-remove-${finding.defect_id}`}
                >
                  Remove
                </button>
                {report.report_config.priority_override && (
                  <>
                    <button
                      onClick={() =>
                        updateConfig((config) => ({
                          ...config,
                          priority_finding_ids: moveItem(
                            config.priority_finding_ids,
                            index,
                            -1,
                          ),
                        }))
                      }
                      className="tap grid h-8 w-8 place-items-center rounded-md border border-slate-200"
                      data-testid={`report-priority-move-up-${finding.defect_id}`}
                    >
                      <ArrowUp className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() =>
                        updateConfig((config) => ({
                          ...config,
                          priority_finding_ids: moveItem(
                            config.priority_finding_ids,
                            index,
                            1,
                          ),
                        }))
                      }
                      className="tap grid h-8 w-8 place-items-center rounded-md border border-slate-200"
                      data-testid={`report-priority-move-down-${finding.defect_id}`}
                    >
                      <ArrowDown className="h-3.5 w-3.5" />
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {activeFindings
              .filter(
                (finding) =>
                  !report.report_config.priority_finding_ids.includes(
                    finding.defect_id,
                  ),
              )
              .map((finding) => (
                <button
                  key={finding.defect_id}
                  onClick={() =>
                    updateConfig((config) => ({
                      ...config,
                      priority_override: true,
                      priority_finding_ids: [
                        ...(config.priority_override
                          ? config.priority_finding_ids
                          : activeFindings
                              .sort(
                                (a, b) =>
                                  SEVERITY_ORDER[a.severity] -
                                  SEVERITY_ORDER[b.severity],
                              )
                              .slice(0, 5)
                              .map((entry) => entry.defect_id)),
                        finding.defect_id,
                      ]
                        .filter((id, index, ids) => ids.indexOf(id) === index)
                        .slice(0, 10),
                    }))
                  }
                  className="tap rounded-md border border-slate-200 px-2 py-1 text-xs font-semibold"
                  data-testid={`report-priority-add-${finding.defect_id}`}
                >
                  + {finding.number || "PC-NEW"}
                </button>
              ))}
          </div>
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="overline">Rooms and room sections</div>
              <p className="mt-1 text-sm text-slate-600">
                Room status and totals are calculated from the item outcomes
                below.
              </p>
            </div>
            <button
              onClick={addRoom}
              className="tap flex h-10 items-center gap-2 rounded-md border border-slate-900 px-4 text-sm font-semibold"
              data-testid="report-add-room-button"
            >
              <Plus className="h-4 w-4" />
              Add room
            </button>
          </div>
          <div className="space-y-4">
            {report.rooms.map((room) => (
              <div
                key={room.room_id}
                className="rounded-lg border border-slate-200 p-4"
                data-testid={`report-editor-room-${room.room_id}`}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    value={room.name}
                    onChange={(event) =>
                      updateRoom(room.room_id, { name: event.target.value })
                    }
                    className="h-10 max-w-sm font-semibold"
                    data-testid={`report-room-name-${room.room_id}`}
                  />
                  <Input
                    value={room.type}
                    onChange={(event) =>
                      updateRoom(room.room_id, { type: event.target.value })
                    }
                    className="h-10 max-w-[180px]"
                    data-testid={`report-room-type-${room.room_id}`}
                  />
                  <select
                    value={
                      report.report_config.room_status_overrides?.[
                        room.room_id
                      ] || ""
                    }
                    onChange={(event) =>
                      updateConfig((config) => ({
                        ...config,
                        room_status_overrides: {
                          ...(config.room_status_overrides || {}),
                          [room.room_id]: event.target.value,
                        },
                      }))
                    }
                    className="h-10 rounded-md border border-slate-200 px-3 text-sm"
                    data-testid={`report-room-status-${room.room_id}`}
                  >
                    <option value="">Auto status</option>
                    <option value="Attention">Attention</option>
                    <option value="Clear">Clear</option>
                    <option value="Review required">Review required</option>
                  </select>
                  <button
                    onClick={() => toggleRoom(room.room_id)}
                    className="tap h-10 rounded-md border border-slate-200 px-3 text-sm font-semibold"
                    data-testid={`report-room-toggle-${room.room_id}`}
                  >
                    {hiddenRooms.has(room.room_id)
                      ? "Restore room"
                      : "Remove from final report"}
                  </button>
                  <button
                    onClick={() => moveRoom(room.room_id, -1)}
                    className="tap grid h-10 w-10 place-items-center rounded-md border border-slate-200"
                    data-testid={`report-room-move-up-${room.room_id}`}
                  >
                    <ArrowUp className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => moveRoom(room.room_id, 1)}
                    className="tap grid h-10 w-10 place-items-center rounded-md border border-slate-200"
                    data-testid={`report-room-move-down-${room.room_id}`}
                  >
                    <ArrowDown className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => addItem(room.room_id)}
                    className="tap h-10 rounded-md border border-slate-900 px-3 text-sm font-semibold"
                    data-testid={`report-room-add-item-${room.room_id}`}
                  >
                    Add item
                  </button>
                </div>
                <div className="mt-3 space-y-2">
                  {room.items.map((item) => (
                    <div
                      key={item.item_id}
                      className="grid gap-2 sm:grid-cols-[150px_1fr_140px_auto]"
                    >
                      <Input
                        value={item.category}
                        onChange={(event) =>
                          updateItem(room.room_id, item.item_id, {
                            category: event.target.value,
                          })
                        }
                        data-testid={`report-item-category-${item.item_id}`}
                      />
                      <select
                        value={item.result || ""}
                        onChange={(event) =>
                          updateItem(room.room_id, item.item_id, {
                            result: event.target.value || null,
                          })
                        }
                        className="h-10 rounded-md border border-slate-200 px-3 text-sm"
                        data-testid={`report-item-result-${item.item_id}`}
                      >
                        <option value="">Not assessed</option>
                        {[
                          ["PASS", "Pass"],
                          ["MAJOR", "Major"],
                          ["MINOR", "Minor"],
                          ["COSMETIC", "Cosmetic"],
                          ["OBSERVATION", "Observation"],
                          ["NT", "N/T"],
                          ["NA", "N/A"],
                        ].map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                      <Input
                        value={item.name}
                        onChange={(event) =>
                          updateItem(room.room_id, item.item_id, {
                            name: event.target.value,
                          })
                        }
                        data-testid={`report-item-name-${item.item_id}`}
                      />
                      <button
                        onClick={() => removeItem(room.room_id, item.item_id)}
                        className="tap grid h-9 w-9 place-items-center rounded-md border border-rose-200 text-rose-600"
                        data-testid={`report-item-remove-${item.item_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="overline">Inspection findings</div>
              <p className="mt-1 text-sm text-slate-600">
                Edit findings and recommendations, or attach and remove report
                photos.
              </p>
            </div>
            <button
              onClick={addFinding}
              className="tap flex h-10 items-center gap-2 rounded-md bg-slate-900 px-4 text-sm font-bold text-white"
              data-testid="report-add-finding-button"
            >
              <Plus className="h-4 w-4" />
              Add finding
            </button>
          </div>
          <div className="mt-4 space-y-4">
            {report.defects.map((defect) => (
              <div
                key={defect.defect_id}
                className={`rounded-lg border p-4 ${hiddenFindings.has(defect.defect_id) ? "border-slate-200 bg-slate-50 opacity-75" : "border-slate-200"}`}
                data-testid={`report-editor-finding-${defect.defect_id}`}
              >
                <div className="grid gap-3 sm:grid-cols-3">
                  <Field
                    label="PC / Finding ID"
                    value={defect.number || ""}
                    testid={`report-finding-number-${defect.defect_id}`}
                    onChange={(value) =>
                      updateFinding(defect.defect_id, {
                        number: value.toUpperCase(),
                      })
                    }
                  />
                  <Field
                    label="Finding"
                    value={defect.name}
                    testid={`report-finding-name-${defect.defect_id}`}
                    onChange={(value) =>
                      updateFinding(defect.defect_id, { name: value })
                    }
                  />
                  <div>
                    <Label className="text-xs">Room</Label>
                    <select
                      value={defect.room_id || ""}
                      onChange={(event) => {
                        const room = report.rooms.find(
                          (entry) => entry.room_id === event.target.value,
                        );
                        updateFinding(defect.defect_id, {
                          room_id: room?.room_id,
                          room: room?.name || "",
                        });
                      }}
                      className="mt-1.5 h-10 w-full rounded-md border border-slate-200 px-3"
                      data-testid={`report-finding-room-${defect.defect_id}`}
                    >
                      {report.rooms.map((room) => (
                        <option key={room.room_id} value={room.room_id}>
                          {room.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">Severity</Label>
                    <select
                      value={defect.severity}
                      onChange={(event) =>
                        updateFinding(defect.defect_id, {
                          severity: event.target.value,
                        })
                      }
                      className="mt-1.5 h-10 w-full rounded-md border border-slate-200 px-3"
                      data-testid={`report-finding-severity-${defect.defect_id}`}
                    >
                      {["MAJOR", "MINOR", "COSMETIC", "OBSERVATION"].map(
                        (severity) => (
                          <option key={severity}>{severity}</option>
                        ),
                      )}
                    </select>
                  </div>
                </div>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <div>
                    <Label className="text-xs">Remark</Label>
                    <Textarea
                      rows={3}
                      value={defect.remark || ""}
                      onChange={(event) =>
                        updateFinding(defect.defect_id, {
                          remark: event.target.value,
                        })
                      }
                      data-testid={`report-finding-remark-${defect.defect_id}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Recommendation</Label>
                    <Textarea
                      rows={3}
                      value={defect.recommendation || ""}
                      onChange={(event) =>
                        updateFinding(defect.defect_id, {
                          recommendation: event.target.value,
                        })
                      }
                      data-testid={`report-finding-recommendation-${defect.defect_id}`}
                    />
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => toggleFindingVisibility(defect.defect_id)}
                    className="tap flex h-10 items-center gap-2 rounded-md border border-slate-200 px-3 text-sm font-semibold"
                    data-testid={`report-finding-toggle-${defect.defect_id}`}
                  >
                    {hiddenFindings.has(defect.defect_id) ? (
                      <Eye className="h-4 w-4" />
                    ) : (
                      <EyeOff className="h-4 w-4" />
                    )}
                    {hiddenFindings.has(defect.defect_id)
                      ? "Restore"
                      : "Hide from report"}
                  </button>
                  <button
                    onClick={() => moveFinding(defect.defect_id, -1)}
                    className="tap grid h-10 w-10 place-items-center rounded-md border border-slate-200"
                    data-testid={`report-finding-move-up-${defect.defect_id}`}
                  >
                    <ArrowUp className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => moveFinding(defect.defect_id, 1)}
                    className="tap grid h-10 w-10 place-items-center rounded-md border border-slate-200"
                    data-testid={`report-finding-move-down-${defect.defect_id}`}
                  >
                    <ArrowDown className="h-4 w-4" />
                  </button>
                  {(defect.photos || []).map((photo) => (
                    <div key={photo} className="relative">
                      <img
                        src={photoSrc(photo)}
                        alt="Report evidence"
                        className="h-16 w-20 rounded-md border object-cover"
                      />
                      <button
                        onClick={() =>
                          updateFinding(defect.defect_id, {
                            photos: defect.photos.filter(
                              (item) => item !== photo,
                            ),
                          })
                        }
                        className="tap absolute -right-2 -top-2 grid h-6 w-6 place-items-center rounded-full bg-rose-600 text-white"
                        data-testid={`report-finding-photo-remove-${photo}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                  <label
                    className="tap flex h-10 cursor-pointer items-center gap-2 rounded-md border border-slate-900 px-3 text-sm font-semibold"
                    data-testid={`report-finding-photo-upload-${defect.defect_id}`}
                  >
                    <Upload className="h-4 w-4" />
                    {uploading === defect.defect_id ? "Uploading" : "Add photo"}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(event) =>
                        uploadPhoto(defect.defect_id, event.target.files?.[0])
                      }
                      data-testid={`report-finding-photo-input-${defect.defect_id}`}
                    />
                  </label>
                  <select
                    value={mergeTargets[defect.defect_id] || ""}
                    onChange={(event) =>
                      setMergeTargets((current) => ({
                        ...current,
                        [defect.defect_id]: event.target.value,
                      }))
                    }
                    className="h-10 max-w-[190px] rounded-md border border-slate-200 px-3 text-sm"
                    data-testid={`report-finding-merge-target-${defect.defect_id}`}
                  >
                    <option value="">Merge into…</option>
                    {report.defects
                      .filter((entry) => entry.defect_id !== defect.defect_id)
                      .map((entry) => (
                        <option key={entry.defect_id} value={entry.defect_id}>
                          {entry.number || "PC-NEW"} · {entry.name}
                        </option>
                      ))}
                  </select>
                  <button
                    onClick={() => mergeFinding(defect.defect_id)}
                    className="tap flex h-10 items-center gap-2 rounded-md border border-slate-900 px-3 text-sm font-semibold"
                    data-testid={`report-finding-merge-${defect.defect_id}`}
                  >
                    <Merge className="h-4 w-4" />
                    Merge
                  </button>
                  <button
                    onClick={() => removeFinding(defect.defect_id)}
                    className="tap ml-auto flex h-10 items-center gap-2 rounded-md border border-rose-200 px-3 text-sm font-semibold text-rose-600"
                    data-testid={`report-finding-remove-${defect.defect_id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete source finding
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
